import { ReservablePackage } from '@go-shared/types/reservable';
import { ObjectId } from 'bson';
import { FormikProps } from 'formik';
import React, { useState, useEffect } from 'react';
import { BubbleButton, BubbleImageThumbnail, BubbleInput } from './BubbleUI';
import { ReservationFormikForm } from './Formik/ReservationFormik';
import { cherrypickImages } from '@go-client/lib/api/image';
import { populate } from '@go-shared/lib/image';
import { mapBy } from '@go-shared/lib/functional';
import { Image } from '@go-shared/types/image';
import { notUndefined, onlyUnique } from '@go-shared/lib/functional';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { formatCentsToUSD } from '@go-shared/lib/utils';

export interface ReservationPackageFillerProps {
  formik: FormikProps<ReservationFormikForm>;
  options: ReservablePackage[];
}

export default function ReservationPackageFiller(props: ReservationPackageFillerProps) {
  const { formik, options } = props;

  const { brand } = useServerInfo();
  const [images, setImages] = useState<Image[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);

  async function fetchData() {
    setLoadingImages(true);

    try {
      const images = await cherrypickImages(
        options
          .map(o => o.images)
          .flat()
          .filter(onlyUnique)
          .filter(notUndefined)
      );
      const populatedCherryPickImages = images.map(i => populate(i, brand.imageTypes));
      setImages(populatedCherryPickImages);
    } finally {
      setLoadingImages(false);
    }
  }

  useEffect(() => {
    fetchData().catch(console.error);
  }, []);

  const imagesMap = mapBy(images, i => i.key);

  return (
    <div className="reservation-package-filler grid grid-cols-3 gap-4">
      {options.map(pkg => {
        const existingPackageIdx = formik.values.packages.findIndex(p => p.packageId === pkg._id);
        const existingPackage = formik.values.packages[existingPackageIdx];
        const quantity = existingPackage?.quantity ?? 0;

        return (
          <div key={pkg._id} className="bg-white p-4 rounded shadow flex flex-col">
            <div className="flex items-start mb-3">
              <BubbleImageThumbnail
                className="w-16 mr-2"
                purpose="plain"
                thumbnail={imagesMap[pkg.images[0]]}
                loading={loadingImages}
              />
              <div className="flex-grow grid grid-cols-1">
                <h2 className="font-semibold">{pkg.name}</h2>
                <p>
                  {formatCentsToUSD(pkg.price)}
                  {pkg.pricePerTicket ? ' (per ticket)' : ''}
                </p>
              </div>
            </div>
            <div
              className={`mt-auto flex items-center rounded-lg overflow-hidden border ${existingPackageIdx < 0 ? 'border-navy' : ''}`}
            >
              {existingPackageIdx < 0 ? (
                <>
                  <BubbleButton
                    block
                    variant="white"
                    onClick={() =>
                      formik.setFieldValue('packages', [
                        ...formik.values.packages,
                        {
                          _id: new ObjectId().toHexString(),
                          packageId: pkg._id,
                          quantity: 1,
                          _package: pkg,
                        },
                      ])
                    }
                    icon="plus"
                  />
                </>
              ) : (
                <>
                  <BubbleButton
                    icon={quantity > 1 ? 'minus' : 'trash-alt'}
                    variant={quantity > 1 ? 'smoke' : 'crimson'}
                    appearance="squared"
                    onClick={() =>
                      quantity > 1
                        ? formik.setFieldValue(
                            `packages[${existingPackageIdx}].quantity`,
                            Math.max(0, quantity - 1)
                          )
                        : formik.setFieldValue(
                            'packages',
                            formik.values.packages.filter(p => p.packageId !== pkg._id)
                          )
                    }
                  />
                  <BubbleInput
                    type="number"
                    block
                    formik={[formik, `packages[${existingPackageIdx}].quantity`]}
                    className="flex-grow"
                    inputClassName="rounded-0-i"
                    disabled
                    value={quantity}
                    min={0}
                    max={1}
                  />
                  <BubbleButton
                    icon={quantity < 1 ? 'plus' : undefined}
                    variant="slate"
                    appearance="squared"
                    disabled={quantity > 0}
                    onClick={() =>
                      formik.setFieldValue(`packages[${existingPackageIdx}].quantity`, quantity + 1)
                    }
                  >
                    {quantity > 0 ? 'MAX' : null}
                  </BubbleButton>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
